import React, { useState } from "react";
import { Share2, Check, Link as LinkIcon } from "lucide-react";

/**
 * Generic share button. Uses Web Share on phones, falls back to clipboard.
 *
 * Passing `app` (fm | flip | pulse | play | astro | contractors) turns the
 * shared link into a Fliboe share-card URL:
 *   /api/share?app=<app>&url=<path>&title=...&desc=...&badge=...
 * That backend page serves a 1200×1200 branded OG card + proper Twitter
 * metadata, then bounces real users to the intended path. Every shared
 * link ends up with a stunning square preview instead of the FM logo.
 *
 * Props:
 *   title, text — human copy (headline + subtitle on the card)
 *   url         — target path (defaults to current)
 *   app         — sub-app theme (falls back to raw url share when omitted)
 *   badge       — small pill on the top-right of the card (e.g. sign glyph, "PDF")
 *   cover       — optional URL of an image (e.g. book cover) composited onto the card
 *   showCopy    — also render a small "Copy link" button next to Share
 *   variant     — "button" | "compact"
 */
export default function ShareButton({ title, text, url, app, badge, cover, showCopy, variant = "button", testid }) {
  const [copied, setCopied] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const buildShareHref = () => {
    if (typeof window === "undefined") return url || "";
    const origin = window.location.origin;
    let target = url;
    if (!target) target = window.location.pathname + window.location.search + window.location.hash;
    // If a full URL was passed, extract its pathname; only bounce same-origin paths.
    try {
      if (target && /^https?:\/\//i.test(target)) {
        const u = new URL(target);
        target = u.origin === origin ? (u.pathname + u.search + u.hash) : target;
      }
    } catch (_) {}
    if (!app || (target && /^https?:\/\//i.test(target))) {
      // No sub-app theme or external URL — share as-is (old behaviour)
      return target && target.startsWith("/") ? origin + target : target || origin;
    }
    const params = new URLSearchParams();
    params.set("app", app);
    params.set("url", target || "/");
    if (title) params.set("title", title);
    if (text) params.set("desc", text);
    if (badge) params.set("badge", badge);
    if (cover) {
      // Cover URL might be relative (e.g. /api/audio/xyz) — make it absolute so the server can fetch it.
      let abs = cover;
      if (abs.startsWith("/")) abs = origin + abs;
      params.set("cover", abs);
    }
    return `${origin}/api/share?${params.toString()}`;
  };

  const onClick = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    const href = buildShareHref();
    if (typeof navigator !== "undefined" && navigator.share) {
      try { await navigator.share({ title, text, url: href }); return; } catch (_) {}
    }
    try {
      await navigator.clipboard.writeText(`${text || title || ""} ${href}`.trim());
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };

  const onCopy = async (e) => {
    e?.preventDefault?.(); e?.stopPropagation?.();
    try {
      await navigator.clipboard.writeText(buildShareHref());
      setLinkCopied(true); setTimeout(() => setLinkCopied(false), 1500);
    } catch (_) {}
  };

  if (variant === "compact") {
    return (
      <button
        onClick={onClick}
        title={copied ? "Link copied!" : "Share"}
        aria-label="Share"
        className="w-8 h-8 rounded-full bg-black/60 border border-white/10 hover:bg-emerald-500 hover:text-black hover:border-emerald-500 text-white flex items-center justify-center transition backdrop-blur-md"
        data-testid={testid}
      >
        {copied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
      </button>
    );
  }
  return (
    <div className="inline-flex gap-2 items-center">
      <button onClick={onClick} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid={testid}>
        {copied ? (<><Check className="w-4 h-4 text-emerald-500" /> Link copied</>) : (<><Share2 className="w-4 h-4 text-emerald-500" /> Share</>)}
      </button>
      {showCopy && (
        <button onClick={onCopy} className="fl-btn ghost !py-2 !px-3 text-sm" data-testid={testid ? `${testid}-copy` : "share-copy-link"}>
          {linkCopied ? (<><Check className="w-4 h-4 text-emerald-400" /> Copied!</>) : (<><LinkIcon className="w-4 h-4 text-emerald-500" /> Copy link</>)}
        </button>
      )}
    </div>
  );
}
